import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppIcon } from './AppIcon';
import { APP_VERSION } from './version';
import { barcelonaRegistrySummary, offlineCityPacks, offlineRegistryVersion } from './data/offlinePacks';
import { formatBytes, requireMapStorage, getOfflineDiagnostics, installBarcelonaCatalog, installBarcelonaMapPack, prepareOfflineDevice, getInstalledMapPack, installMapPack, type OfflineDiagnostics } from './offline';

function yesNo(value: boolean | null) {
  if (value == null) return 'No confirmado';
  return value ? 'Sí' : 'No';
}

export default function OfflinePackageEnhancement() {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [diagnostics, setDiagnostics] = useState<OfflineDiagnostics | null>(null);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState('');
  const [installedPackIds, setInstalledPackIds] = useState<string[]>([]);
  const originalButtonRef = useRef<HTMLButtonElement | null>(null);
  const originalDisplayRef = useRef('');
  const entryButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    let lastHost: HTMLElement | null = null;
    let scheduled = false;

    const restoreOriginal = () => {
      if (originalButtonRef.current) originalButtonRef.current.style.display = originalDisplayRef.current;
      originalButtonRef.current = null;
      originalDisplayRef.current = '';
    };

    const locate = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        const nextHost = document.querySelector<HTMLElement>('.more-grid');
        if (nextHost !== lastHost) {
          restoreOriginal();
          lastHost = nextHost;
          setHost(nextHost);
          const original = nextHost
            ? [...nextHost.querySelectorAll<HTMLButtonElement>(':scope > button')].find((button) => button.textContent?.includes('Estado offline')) ?? null
            : null;
          originalButtonRef.current = original;
          if (original) {
            originalDisplayRef.current = original.style.display;
            original.style.display = 'none';
          }
        }
      });
    };

    locate();
    const observer = new MutationObserver(locate);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      restoreOriginal();
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const appShell = document.querySelector<HTMLElement>('.app-shell');
    const wasInert = appShell?.hasAttribute('inert') ?? false;
    document.body.classList.add('offline-registry-open');
    appShell?.setAttribute('inert', '');
    closeButtonRef.current?.focus();
    void refreshDiagnostics();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('offline-registry-open');
      if (!wasInert) appShell?.removeAttribute('inert');
      window.removeEventListener('keydown', onKeyDown);
      entryButtonRef.current?.focus();
    };
  }, [open]);

  async function refreshDiagnostics() {
    try {
      const next = await getOfflineDiagnostics();
      const ready = await Promise.all(offlineCityPacks.filter((pack) => pack.phase === 'map-ready').map(async (pack) => {
        try { return (await getInstalledMapPack(pack.id)) ? pack.id : null; } catch { return null; }
      }));
      setDiagnostics(next);
      setInstalledPackIds(ready.filter((id): id is string => Boolean(id)));
    } catch {
      setMessage('No fue posible leer el estado de almacenamiento del dispositivo.');
    }
  }

  async function prepare() {
    setWorking(true);
    setMessage('Preparando almacenamiento offline…');
    try {
      await requireMapStorage();
      await prepareOfflineDevice();
      await installBarcelonaCatalog();
      const installation = await installBarcelonaMapPack((received, total) => {
        const percent = total > 0 ? Math.min(100, Math.round((received / total) * 100)) : 0;
        setMessage(`Descargando mapa offline de Barcelona… ${percent}% · ${formatBytes(received)} / ${formatBytes(total)}`);
      });
      const next = await getOfflineDiagnostics();
      setDiagnostics(next);
      setMessage(`Barcelona lista sin conexión · ${formatBytes(installation.bytes)} · SHA-256 ${installation.sha256.slice(0, 12)}… · ${installation.runtimeAssets} recursos de mapa verificados.`);
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'Error no identificado';
      setMessage(`La preparación no pudo completarse: ${detail}. El Wallet y los datos del viaje no fueron modificados.`);
    } finally {
      await refreshDiagnostics();
      setWorking(false);
    }
  }

  async function installCity(packId: string) {
    const pack = offlineCityPacks.find((candidate) => candidate.id === packId);
    if (!pack) return;
    setWorking(true);
    setMessage(`Preparando ${pack.city}…`);
    try {
      await prepareOfflineDevice();
      if (packId === 'barcelona') await installBarcelonaCatalog();
      const result = await installMapPack(packId, (received, total) => {
        const percent = total > 0 ? Math.min(100, Math.round((received / total) * 100)) : 0;
        setMessage(`Descargando ${pack.city}… ${percent}% · ${formatBytes(received)} / ${formatBytes(total)}`);
      });
      setMessage(`${pack.city} lista sin conexión · ${formatBytes(result.bytes)} · integridad verificada.`);
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'Error no identificado';
      setMessage(`No fue posible instalar ${pack.city}: ${detail}.`);
    } finally {
      await refreshDiagnostics();
      setWorking(false);
    }
  }

  const readiness = useMemo(() => {
    if (!diagnostics) return 'Revisar dispositivo';
    if (diagnostics.shellCached && diagnostics.walletCached && diagnostics.barcelonaCatalogInstalled && diagnostics.barcelonaMapInstalled) return 'Barcelona offline lista';
    if (diagnostics.shellCached && diagnostics.walletCached) return 'Base offline lista · falta mapa';
    return 'Preparación pendiente';
  }, [diagnostics]);

  if (!host && !open) return null;

  return (
    <>
      {host && createPortal(
        <button ref={entryButtonRef} type="button" className="offline-package-entry" onClick={() => setOpen(true)}>
          <span aria-hidden="true"><AppIcon name="offline" /></span>
          <div><strong>Estado offline</strong><small>{readiness} · paquetes por ciudad</small></div>
          <b><AppIcon name="chevronRight" size={20} /></b>
        </button>,
        host,
      )}
      {open && createPortal(
        <section className="offline-registry-viewer" role="dialog" aria-modal="true" aria-label="Estado offline y paquetes por ciudad">
          <header className="offline-registry-toolbar">
            <div><p className="eyebrow">OFFLINE</p><h2>Preparar viaje</h2></div>
            <button ref={closeButtonRef} type="button" onClick={() => setOpen(false)} aria-label="Cerrar">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-x" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="m15 9-6 6" />
              <path d="m9 9 6 6" />
            </svg>
          </button>
          </header>

          <div className="offline-registry-scroll">
            <section className="offline-device-card">
              <div><strong>{readiness}</strong><small>{diagnostics?.standalone ? 'PWA instalada' : 'Abrir como PWA instalada para la prueba final'}</small></div>
              <button type="button" onClick={() => void prepare()} disabled={working}>{working ? 'Preparando…' : diagnostics?.barcelonaMapInstalled ? 'Verificar de nuevo' : 'Descargar Barcelona'}</button>
              <dl>
                <div><dt>Service Worker</dt><dd>{diagnostics?.serviceWorkerControlled ? 'Activo' : 'Pendiente'}</dd></div>
                <div><dt>Shell</dt><dd>{diagnostics?.shellCached ? 'En caché' : 'Pendiente'}</dd></div>
                <div><dt>Wallet</dt><dd>{diagnostics?.walletCached ? 'En caché' : 'Pendiente'}</dd></div>
                <div><dt>OPFS</dt><dd>{diagnostics?.opfsSupported ? 'Disponible' : 'No disponible'}</dd></div>
                <div><dt>Mapa Barcelona</dt><dd>{diagnostics?.barcelonaMapInstalled ? formatBytes(diagnostics.barcelonaMapBytes) : 'Pendiente'}</dd></div>
                <div><dt>Persistencia</dt><dd>{yesNo(diagnostics?.persistentStorage ?? null)}</dd></div>
                <div><dt>Uso</dt><dd>{formatBytes(diagnostics?.usageBytes ?? null)} / {formatBytes(diagnostics?.quotaBytes ?? null)}</dd></div>
              </dl>
              {diagnostics?.opfsError && <p className="offline-registry-message" role="status">{diagnostics.opfsError}</p>}
              {diagnostics?.barcelonaMapSha256 && <code>PMTiles SHA-256 {diagnostics.barcelonaMapSha256.slice(0, 16)}…</code>}
              {message && <p className="offline-registry-message" role="status">{message}</p>}
            </section>

            <section className="offline-city-section">
              <header><div><strong>Mapas del viaje</strong><small>{offlineRegistryVersion} · {barcelonaRegistrySummary.locationCount} POI · {barcelonaRegistrySummary.routeCount} rutas</small></div><span>{offlineCityPacks.length}</span></header>
              <div className="offline-city-list">
                {offlineCityPacks.map((pack) => {
                  const installed = installedPackIds.includes(pack.id);
                  const installable = pack.phase === 'map-ready' && Boolean(pack.basemapUrl);
                  return (
                    <article key={pack.id} className={pack.phase !== 'planned' ? 'prototype' : ''}>
                      <div><strong>{pack.city}</strong><small>{pack.country}</small><p>{formatBytes(pack.basemapBytes ?? null)} · {installed ? 'Disponible sin conexión' : 'Descarga individual'}</p></div>
                      {installable ? <button type="button" className="offline-city-action" disabled={working} onClick={() => void installCity(pack.id)}>{installed ? 'Verificar' : 'Descargar'}</button> : <span>Próximamente</span>}
                    </article>
                  );
                })}
              </div>
            </section>

            <p className="offline-registry-footnote">V{APP_VERSION} instala cada mapa elegido en OPFS y comprueba su integridad antes de dejarlo disponible sin conexión.</p>
          </div>
        </section>,
        document.body,
      )}
    </>
  );
}

